// src/tenantAdmin/hooks/useTenantUsers.js
//
// The Utenti tab's one read: GET /tenant-admin/users for ONE workspace, plus the
// seat figure off the same envelope. Extracted from the page so the workspace
// selector has somewhere safe to point.
//
// ⛔⛔ STALE ANSWERS ARE DROPPED, not merged. With a selector above the table two
// loads can be in flight at once, and the slow one is not always the old one: a
// late answer for workspace A landing in the table while the header says B would
// show one company's people under another's name — and every row action would
// then be aimed at the workspace in the URL, not the one on screen. Each load
// takes a ticket (`seq`) and anything that is not the latest is discarded.
//
// ⛔ A workspace change CLEARS the rows before loading. Keeping the previous
// workspace's rows on screen "until the new ones arrive" is the same defect held
// for a shorter time.
//
// ⚠️ `seats` is the COMPANY's people against its ceiling, read by its own
// UNFILTERED request (fetchTenantSeats), precisely so the email search can never
// leak into it. `used: null` is UNKNOWN, never zero.
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { listTenantUsers, fetchTenantSeats } from '../tenantAdminApi.js';
import { userActionErrorCopy } from '../userActionError.js';

// "Not told": what the seat tile reads before the envelope lands, without a
// workspace, and when the read fails. ⛔ `used: null` is UNKNOWN, never zero — it
// hides the tile instead of printing a figure nobody stands behind.
export const SEATS_UNKNOWN = { used: null, limit: null, source: null };

const EMPTY = { users: [], companyAdmins: [], total: 0 };

export default function useTenantUsers(tenantId, { limit = 20, offset = 0, email } = {}) {
  const { t } = useTranslation('admin');
  const [rows, setRows] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [seats, setSeats] = useState(SEATS_UNKNOWN);
  // One ticket counter per read. They are separate because the two requests are
  // separate: a seat answer is not invalidated by a list reload.
  const listSeq = useRef(0);
  const seatSeq = useRef(0);

  const reload = useCallback(async () => {
    if (!tenantId) return;
    const mine = ++listSeq.current;
    setLoading(true);
    setError('');
    try {
      const data = await listTenantUsers(tenantId, { limit, offset, email: email || undefined });
      if (mine !== listSeq.current) return;
      const all = data?.users || [];
      // The company's own administrators ride along on EVERY page, unpaginated
      // and uncounted in `total`, and every per-user route 404s on them — so they
      // leave the member table here and are rendered read-only elsewhere.
      setRows({
        users: all.filter(r => r.via !== 'customer'),
        companyAdmins: all.filter(r => r.via === 'customer'),
        total: data?.total ?? 0,
      });
    } catch (err) {
      // The ticket guards the failure and `loading` too: a stale error must not
      // paint over fresh rows, nor a stale answer end the live load's spinner.
      if (mine !== listSeq.current) return;
      setError(userActionErrorCopy(t, err));
    } finally {
      if (mine === listSeq.current) setLoading(false);
    }
  }, [tenantId, limit, offset, email, t]);

  // Deliberately NOT keyed on `offset` or `email`: the seat figure is a property
  // of the company, not of the page being looked at. It reloads on a workspace
  // switch and after every write that can move it.
  const reloadSeats = useCallback(async () => {
    const mine = ++seatSeq.current;
    if (!tenantId) { setSeats(SEATS_UNKNOWN); return; }
    try {
      const next = await fetchTenantSeats(tenantId);
      if (mine === seatSeq.current) setSeats(next);
    } catch {
      // Fail open, like every other entitlement read: an unknown figure hides
      // the seat line and blocks nothing. The 409 backstop still holds.
      if (mine === seatSeq.current) setSeats(SEATS_UNKNOWN);
    }
  }, [tenantId]);

  // ⛔ Declared BEFORE the two load effects so it runs first in the same commit:
  // the rows and the figure on screen belong to the workspace we just left.
  useEffect(() => {
    setRows(EMPTY);
    setSeats(SEATS_UNKNOWN);
    setError('');
    setLoading(true);
  }, [tenantId]);

  useEffect(() => { reload(); }, [reload]);
  useEffect(() => { reloadSeats(); }, [reloadSeats]);

  return {
    users: rows.users,
    companyAdmins: rows.companyAdmins,
    total: rows.total,
    loading,
    error,
    seats,
    reload,
    reloadSeats,
  };
}
